import { zodResolver } from "@hookform/resolvers/zod";
import SearchIcon from '@mui/icons-material/Search';
import { Box, ListItemIcon, MenuItem, Stack, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import Icon from "../../@core/components/icon";

import { AsyncSelect, ControlledTextField } from "../../components/forms";
import { ControlledAutocomplete } from "../../components/forms/ControlledAutocomplete";
import { ControlledDateRangePicker } from "../../components/forms/ControlledDateRangePicker";
import { ITableColumn, TableWithPagingAndFilter } from "../../components/table";
import { usePagination } from "../../hooks/usePagination";

import {
    EntityWithIdAndDescription,
    EntityWithIdAndDescriptionFields,
    QueryStatus
} from "../../types";
import { dateFormatter } from "../../utils/formatters";
import { HttpSupplierOrders } from "./api/HttpSupplierOrders";
import SupplierOrderStatusChip from "./components/SupplierOrderChip";
import SupplierOrderDialog from './components/SupplierOrderDialog';
import { schemaSupplierOrderFilter, SupplierOrderFilterType, SupplierOrderViewDTO } from "./types/dto";
import { SupplierOrderStatusTypes } from "./types/enum";
import { SupplierOrderFields, SupplierOrderFilterFields } from "./types/fields";
import { getSupplierOrderStatusColor } from "./types/typification";

function SupplierOrders() {
    const [showFilter, setShowFilter] = useState<boolean>(false)
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [selectedOrder, setSelectedOrder] = useState<SupplierOrderViewDTO>()
    const [orderList, setOrderList] = useState<SupplierOrderViewDTO[]>([]);

    const onSearch = (data: SupplierOrderFilterType) => {
        setFilterAndSearch(data)
    }

    const { control, getValues, handleSubmit } = useForm<SupplierOrderFilterType>({
        defaultValues: {
            [SupplierOrderFilterFields.StatusCodeList]: [SupplierOrderStatusTypes.PENDING_ORDER, SupplierOrderStatusTypes.PENDING_DELIVERY],
            [SupplierOrderFields.Items]: undefined,
        },
        resolver: zodResolver(schemaSupplierOrderFilter)
    })

    const {
        entities: orders,
        status: ordersStatus,
        paginacion: pagination,
        onChange,
        refetch,
        setFilterAndSearch
    } = usePagination<SupplierOrderViewDTO, SupplierOrderFilterType>({
        initialFilter: { ...getValues() },
        loader: (pagination, filter) => HttpSupplierOrders.get(pagination, filter)
    })

    useEffect(() => {
        setOrderList(orders)
    }, [orders])

    const columns: ITableColumn[] = [
        {
            label: "Pedido",
            width: '15%',
            onRenderCell: (order: SupplierOrderViewDTO) => (
                <Tooltip title={order[SupplierOrderFields.Items] || ''}>
                    <Typography
                        variant={'body2'}
                        sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontWeight: 600
                        }}
                    >
                        {order[SupplierOrderFields.Items]}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            label: "Cliente / Transporte",
            onRenderCell: (order: SupplierOrderViewDTO) => {
                const content =

                    <Stack direction={'column'}
                        sx={{ maxWidth: '200px' }}
                    >
                        <Typography variant='body2' noWrap>
                            <b>Cliente:</b> {order[SupplierOrderFields.DestinationClient]}
                        </Typography>
                        {order[SupplierOrderFields.Transport] && (
                            <Typography variant='caption' noWrap color="textSecondary">
                                Transp: {order[SupplierOrderFields.Transport]}
                            </Typography>
                        )}
                    </Stack>

                return (
                    <Tooltip title={`${order[SupplierOrderFields.DestinationClient]} - ${order[SupplierOrderFields.Transport] || '-'}`}>
                        {content}
                    </Tooltip>
                )
            },
        },
        {
            label: "Proveedor",
            width: '10%',
            onRenderCell: (order: SupplierOrderViewDTO) => (
                <Stack direction={'column'}>
                    <Typography variant='caption'>{order[SupplierOrderFields.Provider] || '-'}</Typography>
                    <Typography variant='caption'>Canal: {order[SupplierOrderFields.CommunicationChannel] || '-'}</Typography>
                </Stack>
            ),
        },
        {
            label: "Fechas",
            width: '15%',
            onRenderCell: (row: SupplierOrderViewDTO) => (
                <Stack>
                    <Typography variant='body2' sx={{ fontSize: '0.6rem' }}>
                        <b>Carga:</b> {row[SupplierOrderFields.CreatedDate] ? dateFormatter.toShortDate(row[SupplierOrderFields.CreatedDate]) : '-'}
                    </Typography>
                    <Typography variant='body2' sx={{ fontSize: '0.6rem' }}>
                        <b>Pedido:</b> {row[SupplierOrderFields.RequestDate] ? dateFormatter.toShortDate(row[SupplierOrderFields.RequestDate]) : '-'}
                    </Typography>
                    <Typography variant='body2' sx={{ fontSize: '0.6rem' }}>
                        <b>Entrega:</b> {row[SupplierOrderFields.DeliveryDate] ? dateFormatter.toShortDate(row[SupplierOrderFields.DeliveryDate]) : '-'}
                    </Typography>
                </Stack>
            ),
        },
        {
            label: "Responsable",
            width: '10%',
            onRenderCell: (order: SupplierOrderViewDTO) => (
                <Typography variant='caption' sx={{ fontSize: '0.7rem' }}>
                    {order[SupplierOrderFields.ResponsibleUserName] || '-'}
                </Typography>
            ),
        },
        {
            label: "Estado",
            width: '10%',
            onRenderCell: (order: SupplierOrderViewDTO) => (
                <Box sx={{ maxWidth: '165px' }}>

                    <SupplierOrderStatusChip SupplierOrderOption={order} />
                </Box>
            )
        },
        {
            label: "Acciones",
            width: '5%',
            onRenderCell: (order: SupplierOrderViewDTO) => (
                <Tooltip title='Ver / Editar'>
                    <IconButton onClick={() => { setSelectedOrder(order); setDialogOpen(true); }}>
                        <SearchIcon color='primary' />
                    </IconButton>
                </Tooltip>
            )
        }
    ];

    return (
        <>
            <TableWithPagingAndFilter
                title='Pedidos a Proveedores'
                entityPaginada={orderList && pagination ? { lista: orderList, paginacion: pagination } : undefined}
                columns={columns}
                error={ordersStatus === QueryStatus.Error}
                isLoading={ordersStatus === QueryStatus.Loading || ordersStatus === QueryStatus.Idle}
                onShowChildren={() => setShowFilter(!showFilter)}
                showChildren={showFilter}
                childrenName={"Filtros"}
                onPaging={onChange}
                onNewRegister={() => {
                    setSelectedOrder(undefined)
                    setDialogOpen(true)
                }}

            // tableSx={{tableLayout: 'fixed'}}
            >

                <Grid container spacing={4} sx={{ p: 5 }}>
                    <Grid item md={4} sm={6} xs={12}>
                        <ControlledTextField control={control}
                            name={SupplierOrderFilterFields.Items}
                            label={'Contenido del Pedido'}
                            fullWidth
                        />
                    </Grid>
                    <Grid item md={3} sm={5} xs={12}>
                        <ControlledTextField control={control}
                            name={SupplierOrderFilterFields.DestinationClient}
                            label={'Cliente Destino'}
                            fullWidth
                        />
                    </Grid>

                    <Grid item md={3} xs={12}>
                        <AsyncSelect
                            control={control}
                            name={SupplierOrderFields.ProviderId}
                            label="Proveedor"
                            loadOptions={HttpSupplierOrders.getProviders}
                            selectNotNulleable
                            fullWidth
                        />
                    </Grid>

                    <Grid item md={2} xs={12}>
                        <AsyncSelect
                            control={control}
                            name={SupplierOrderFields.ResponsibleUserId}
                            label="Responsable"
                            loadOptions={HttpSupplierOrders.getUsers}
                            fullWidth
                        />
                    </Grid>

                    <Grid item md={6} sm={6} xs={12}>
                        <ControlledAutocomplete
                            loadOptions={HttpSupplierOrders.getStates}
                            control={control}
                            name={SupplierOrderFilterFields.StatusCodeList}
                            label={'Estado'}
                            valueField={'id'}
                            multiple
                            renderOption={(renderProps, value: EntityWithIdAndDescription, state) => {
                                const color = getSupplierOrderStatusColor(value[EntityWithIdAndDescriptionFields.Id])

                                return (
                                    <MenuItem key={state.index} selected={state.selected} value={value.id} {...renderProps}>
                                        <ListItemIcon>
                                            <Icon icon='mdi:circle' color={color} fontSize='1rem' />
                                        </ListItemIcon>
                                        {value.descripcion}
                                    </MenuItem>
                                )
                            }}
                        />
                    </Grid>

                    <Grid item md={3} sm={12} xs={12}>
                        <ControlledDateRangePicker
                            control={control}
                            label={'Fecha Alta'}
                            startDateName={SupplierOrderFilterFields.CreatedDateStart}
                            endDateName={SupplierOrderFilterFields.CreatedDateEnd}
                            fullWdith
                        />
                    </Grid>

                    <Grid item md={3} sm={12} xs={12}>
                        <ControlledDateRangePicker
                            control={control}
                            label={'Fecha Entrega'}
                            startDateName={SupplierOrderFilterFields.DeliveryDateStart}
                            endDateName={SupplierOrderFilterFields.DeliveryDateEnd}
                            fullWdith
                        />
                    </Grid>

                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant='contained'
                            onClick={handleSubmit(onSearch)}
                            startIcon={<SearchIcon />}
                        >
                            Buscar
                        </Button>
                    </Grid>
                </Grid>
            </TableWithPagingAndFilter>



            {dialogOpen &&
                <SupplierOrderDialog
                    open={dialogOpen}
                    onClose={() => {
                        setSelectedOrder(undefined)
                        setDialogOpen(false)
                    }}
                    onAfterSubmit={() => {
                        refetch()
                        setSelectedOrder(undefined)
                        setDialogOpen(false)
                    }}
                    order={selectedOrder}
                    maxWidth={'md'}
                />}

        </>
    );
}

export default SupplierOrders;
